import { Injectable } from '@angular/core';
import { AddRemoveClientContext } from '@domain/intake-form-context';
import { Action, dispatch, select, State, StateContext } from '@ngxs/store';
import { SyncPortalEngineContextAndResponses } from './portal-engine.actions';
import { firstValueFrom } from 'rxjs';
import { PortalEngineSelectors } from './portal-engine.selectors';
import { EngineFormItemType } from '@models/client-portal/engine-client-intake';
import { ClientIntakeFormType } from '@models/client-intake';
import {
	generateInitialFormResponses,
	DefaultValueContext,
} from '../utils/portal-engine.utils';
import {
	ClientIntakeResponse,
	UpsertClientIntakeResponses,
} from '@domain/intake-form-client-responses';
import { isPersonClient, isBusinessClient } from '@globalUtils/client-intake';
import { PersonMatterClient } from '@models/matter';
import { BusinessClient } from '@models/clients';
import { EngineFormContent } from './portal-engine.models';
export interface PortalEngineStateModel {
	[key: string]: unknown;
}

@State({
	name: 'portalEngine',
	defaults: {},
})
@Injectable()
export class PortalEngineState {
	addRemoveClientContext = dispatch(AddRemoveClientContext);
	upsertClientIntakeResponses = dispatch(UpsertClientIntakeResponses);

	engineData_ = select(PortalEngineSelectors.$engineData);

	@Action(SyncPortalEngineContextAndResponses)
	async syncPortalEngineContextAndResponses(
		ctx: StateContext<PortalEngineStateModel>,
		action: SyncPortalEngineContextAndResponses,
	): Promise<void> {
		const engineData = this.engineData_();
		if (!engineData || !engineData.context || !engineData.currentForm) {
			return;
		}

		const { context, currentResponses, currentForm, engineItems } = engineData;
		const { clients, matter, loggedInUser } = action.payload;

		await firstValueFrom(
			this.addRemoveClientContext({ currentContext: context, clients }),
		);

		const allFieldItems = engineItems.filter(
			(item) => item.type === EngineFormItemType.FIELD,
		);

		const responses: ClientIntakeResponse[] = [];

		// Generate initial responses for general form if not already present
		if (
			!currentResponses.find(
				(response) => response.type === ClientIntakeFormType.GENERAL,
			)
		) {
			const generalFormId = context.general?.id;
			if (!generalFormId) return;

			const generalFormContent: EngineFormContent = {
				id: generalFormId,
				content: allFieldItems,
				formType: ClientIntakeFormType.GENERAL,
				name: context.general?.name || 'General',
			};

			const generalContext: DefaultValueContext = { matter };
			const response = generateInitialFormResponses(
				generalFormContent,
				generalContext,
				loggedInUser,
				currentForm.id,
			);
			responses.push(response);
		}

		// Generate initial responses for each client that is not already in the responses
		clients.forEach((client) => {
			if (
				!currentResponses.find((response) => response.clientId === client.id)
			) {
				const clientFromContext = context.clients?.all.find(
					(c) => c.id === client.id,
				);

				const clientFormContent: EngineFormContent = {
					id: client.id,
					content: allFieldItems,
					formType: ClientIntakeFormType.CLIENT,
					name: clientFromContext?.name || 'Client',
				};

				const clientContext: DefaultValueContext = { matter };
				if (isPersonClient(client)) {
					clientContext.client = client;
					clientContext.matterClient = client as PersonMatterClient;
				} else if (isBusinessClient(client)) {
					clientContext.businessClient = client as BusinessClient;
				}

				const response = generateInitialFormResponses(
					clientFormContent,
					clientContext,
					loggedInUser,
					currentForm.id,
				);
				responses.push(response);
			}
		});

		await firstValueFrom(this.upsertClientIntakeResponses(responses));
	}
}
